import { BEDROCK_MODELS } from "@ligament/shared";
import type { AdapterConfigFieldsProps } from "../types";
import {
  Field,
  DraftInput,
  DraftNumberInput,
} from "../../components/agent-config-primitives";

const inputClass =
  "w-full rounded-md border border-border px-2.5 py-1.5 bg-transparent outline-none text-sm font-mono placeholder:text-muted-foreground/40";

const TIER_BADGES: Record<string, string> = {
  premium: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  standard: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  fast: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300",
};

export function BedrockConfigFields({
  isCreate,
  values,
  set,
  config,
  eff,
  mark,
}: AdapterConfigFieldsProps) {
  // Extra fields beyond standard CreateConfigValues
  const ext = (values as unknown as Record<string, unknown>) ?? {};

  const currentModelId = isCreate
    ? String(ext.modelId ?? "us.anthropic.claude-sonnet-4-20250514-v1:0")
    : eff("adapterConfig", "modelId", String(config.modelId ?? "us.anthropic.claude-sonnet-4-20250514-v1:0"));

  return (
    <>
      <Field
        label="Model"
        hint="Bedrock foundation model for inference. Premium models are more capable but cost more."
      >
        <div className="space-y-1">
          {BEDROCK_MODELS.map((m) => (
            <label
              key={m.id}
              className={`flex items-center gap-3 rounded-md border px-3 py-2 cursor-pointer transition-colors ${
                currentModelId === m.id
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/30 hover:bg-accent/30"
              }`}
            >
              <input
                type="radio"
                name="bedrock-model"
                value={m.id}
                checked={currentModelId === m.id}
                onChange={() =>
                  isCreate
                    ? set!({ modelId: m.id } as never)
                    : mark("adapterConfig", "modelId", m.id)
                }
                className="accent-primary"
              />
              <span className="text-sm font-medium flex-1">{m.label}</span>
              <span
                className={`rounded-full px-1.5 py-px text-[9px] font-medium uppercase tracking-wider ${
                  TIER_BADGES[m.tier] ?? TIER_BADGES.standard
                }`}
              >
                {m.tier}
              </span>
            </label>
          ))}
        </div>
      </Field>

      <Field
        label="AWS Region"
        hint="AWS region for Bedrock API calls. Defaults to the server's AWS_REGION."
      >
        <DraftInput
          value={
            isCreate
              ? String(ext.region ?? "")
              : eff("adapterConfig", "region", String(config.region ?? ""))
          }
          onCommit={(v) =>
            isCreate
              ? set!({ region: v } as never)
              : mark("adapterConfig", "region", v || undefined)
          }
          immediate
          className={inputClass}
          placeholder="us-east-1 (default from environment)"
        />
      </Field>

      <Field
        label="Max output tokens"
        hint="Maximum tokens per model response (default: 4096)"
      >
        {isCreate ? (
          <input
            type="number"
            className={inputClass}
            value={String(ext.maxTokens ?? 4096)}
            onChange={(e) =>
              set!({ maxTokens: Number(e.target.value) } as never)
            }
          />
        ) : (
          <DraftNumberInput
            value={eff(
              "adapterConfig",
              "maxTokens",
              Number(config.maxTokens ?? 4096),
            )}
            onCommit={(v) => mark("adapterConfig", "maxTokens", v || 4096)}
            immediate
            className={inputClass}
          />
        )}
      </Field>

      <Field
        label="Temperature"
        hint="Sampling temperature 0-1 (default: 0.7)"
      >
        {isCreate ? (
          <input
            type="number"
            step="0.1"
            min="0"
            max="1"
            className={inputClass}
            value={String(ext.temperature ?? 0.7)}
            onChange={(e) =>
              set!({ temperature: Number(e.target.value) } as never)
            }
          />
        ) : (
          <DraftNumberInput
            value={eff(
              "adapterConfig",
              "temperature",
              Number(config.temperature ?? 0.7),
            )}
            onCommit={(v) =>
              mark("adapterConfig", "temperature", v ?? 0.7)
            }
            immediate
            className={inputClass}
          />
        )}
      </Field>

      <Field
        label="Guardrail ID"
        hint="Optional Bedrock Guardrail identifier for PHI/PII filtering"
      >
        <DraftInput
          value={
            isCreate
              ? String(ext.guardrailId ?? "")
              : eff(
                  "adapterConfig",
                  "guardrailId",
                  String(config.guardrailId ?? ""),
                )
          }
          onCommit={(v) =>
            isCreate
              ? set!({ guardrailId: v } as never)
              : mark("adapterConfig", "guardrailId", v || undefined)
          }
          immediate
          className={inputClass}
          placeholder="Optional — Bedrock guardrail ID"
        />
      </Field>

      <Field
        label="Max turns per run"
        hint="Maximum tool-use turns per heartbeat execution (default: 20)"
      >
        {isCreate ? (
          <input
            type="number"
            className={inputClass}
            value={values?.maxTurnsPerRun ?? 20}
            onChange={(e) => set!({ maxTurnsPerRun: Number(e.target.value) })}
          />
        ) : (
          <DraftNumberInput
            value={eff(
              "adapterConfig",
              "maxTurnsPerRun",
              Number(config.maxTurnsPerRun ?? 20),
            )}
            onCommit={(v) => mark("adapterConfig", "maxTurnsPerRun", v || 20)}
            immediate
            className={inputClass}
          />
        )}
      </Field>

      <div className="rounded-md border border-emerald-500/20 bg-emerald-500/5 p-3 text-xs text-emerald-400 leading-relaxed">
        <strong>HIPAA-Compliant:</strong> Bedrock uses your AWS IAM role for authentication — no API keys needed.
        All inference stays within your AWS account boundary.
      </div>
    </>
  );
}
